import { useEffect, useState } from 'react';
import { request } from '../../api/client';

interface Health {
  status: string;
  uptime_s: number;
}

type State =
  { kind: 'loading' } | { kind: 'ok'; uptimeS: number } | { kind: 'down'; reason: string };

const PROBE_TTL_MS = 60_000;
let lastProbe: { at: number; result: Promise<Health> } | null = null;

/**
 * GET /healthz through the shared request() (same origin, no token). One probe
 * per minute per page load, shared by the auth and app shells, so switching
 * layouts after sign-in does not probe again. A static host answering the
 * SPA fallback (HTML, 200) counts as unreachable: it is not the API.
 */
export function probeHealth(): Promise<Health> {
  const now = Date.now();
  if (!lastProbe || now - lastProbe.at > PROBE_TTL_MS) {
    const result = request<Health | undefined>('/healthz', { auth: false }).then((body) => {
      if (!body || body.status !== 'ok') throw new Error('not the API');
      return body;
    });
    lastProbe = { at: now, result };
    result.catch(() => {
      lastProbe = null; // a failure is probed again on the next mount
    });
  }
  return lastProbe.result;
}

export function ApiStatus() {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    probeHealth()
      .then((health) => {
        if (!cancelled) setState({ kind: 'ok', uptimeS: health.uptime_s });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'down', reason: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') return <span className="status">API: checking…</span>;
  if (state.kind === 'ok')
    return (
      <span className="status" data-state="ok">
        API: up ({state.uptimeS}s)
      </span>
    );
  return (
    <span className="status" data-state="down">
      API: unreachable ({state.reason})
    </span>
  );
}
